import { useState, useMemo } from "react";
import { cn } from "web-design-system";

const SAFE_EXPR = /^[0-9\s+\-*/%.()^,]+$/;

interface EvalResult {
  display: string;
  raw: number | null;
}

function stripNumberCommas(expr: string): string {
  return expr.replace(/\d[\d,]*\d/g, (match) => match.replace(/,/g, ""));
}

function evaluate(expr: string): EvalResult {
  const trimmed = expr.trim();
  if (!trimmed) return { display: "", raw: null };
  if (!SAFE_EXPR.test(trimmed)) return { display: "Invalid", raw: null };

  try {
    const normalized = stripNumberCommas(trimmed).replace(/\^/g, "**");
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict"; return (${normalized})`)();
    if (typeof result !== "number" || !Number.isFinite(result))
      return { display: "Error", raw: null };
    const fixed = parseFloat(result.toPrecision(5));
    return { display: fixed.toLocaleString("en-IN"), raw: fixed };
  } catch {
    return { display: "", raw: null };
  }
}

export function ExpressionCalculator() {
  const [expression, setExpression] = useState("");

  const { display, raw } = useMemo(() => evaluate(expression), [expression]);

  const isError = display === "Invalid" || display === "Error";

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && raw !== null) {
      setExpression(String(raw));
    }
  }

  return (
    <div className="p-4 pt-3 flex gap-2 flex-col rounded-md bg-card border-secondary border">
      <span className="text-sm font-semibold">Calculator</span>
      <div className="flex items-center w-full bg-background border border-border rounded-md px-3 py-2 gap-2 focus-within:ring-1 focus-within:ring-ring transition-shadow">
        <input
          type="text"
          value={expression}
          onChange={(e) => setExpression(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="E.g. 1200 * 12 * 5"
          spellCheck={false}
          className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground min-w-0"
        />
        {display && (
          <>
            <span className="text-muted-foreground text-sm shrink-0">=</span>
            <span
              className={cn(
                "text-sm font-semibold shrink-0",
                isError ? "text-destructive" : "text-primary"
              )}
            >
              {display}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
